import dynamic from "next/dynamic";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getAllowSendPointsFromStorefront,
  getIsLockerShop,
  getIsStorePointsMigrationAllowed,
  getStoreCurrencyDetails,
} from "@store/selectors/store";

import { useStoreContext } from "@components/store/provider";
import useShowNewHeader from "@hooks/use-show-new-header";

import { useCheckoutContext } from "@contexts/checkout-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import { setStoreBalance } from "@slices/store";
import { getStadiumSharedConfig } from "@helpers/config";
import { getCartOrderAllowAnnonymousInvite, getCartOrderAuthorizedPayments } from "@selectors/cart";
import { convertPrice } from "@helpers/currency";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { isCustomCurrency } from "@stadium/helpers/currency";

import styles from "./main.module.scss";

const PointsNewBalance = dynamic(() =>
  import("@components/store/navigation/header-points-balance"),
);
const FundsDetail = dynamic(() => import("@stadium/shared/src/components/user-funds-detail"));

const RESTRICTED_SOURCES_FOR_BAG_AND_ITEM = ["checkout"];

const UserOldPointsBalance = () => {
  const dispatch = useDispatch();

  const { isGuestOrPreviewMode, previewMode } = usePreviewModeContext();

  const storeDetails = useSelector(getStoreCurrencyDetails);
  const isLockerShop = useSelector(getIsLockerShop);
  const allowSendPointsFromStorefront =
    useSelector(getAllowSendPointsFromStorefront) && !isLockerShop;
  const { storeDisplayCurrencyCode, source } = useStoreContext();
  const authorizedPayments = useSelector(getCartOrderAuthorizedPayments);
  const conversionRates = useConfig(getConversionRates);
  const allowAnonymousInvite = useSelector(getCartOrderAllowAnnonymousInvite);
  const isStorePointsMigrationAllowed =
    useSelector(getIsStorePointsMigrationAllowed) && !allowAnonymousInvite;

  const onPointsTransferSuccess = () => {
    const storePayload = {
      balance: 0,
    };
    dispatch(setStoreBalance(storePayload));
  };

  const {
    cartItemsTotal,
    walletDetails: { walletCurrencyCode },
  } = useCheckoutContext();

  const { convertedCartItemsTotal, cartItemsTotalCurrencyCode } = useMemo(() => {
    if (storeDisplayCurrencyCode && isCustomCurrency(storeDisplayCurrencyCode)) {
      return {
        convertedCartItemsTotal: convertPrice({
          from: storeDisplayCurrencyCode,
          to: walletCurrencyCode,
          value: cartItemsTotal,
          conversionRates,
        }),
        cartItemsTotalCurrencyCode: walletCurrencyCode,
      };
    } else {
      return {
        convertedCartItemsTotal: cartItemsTotal,
        cartItemsTotalCurrencyCode: storeDisplayCurrencyCode,
      };
    }
  }, [storeDisplayCurrencyCode, cartItemsTotal, walletCurrencyCode, conversionRates]);

  const storeData = useMemo(
    () => ({
      ...storeDetails,
      ...getStadiumSharedConfig,
      cartItemsTotal: convertedCartItemsTotal,
      handlePointsTransferSuccess: onPointsTransferSuccess,
      isGuestOrPreviewMode,
      isStorePointsMigrationAllowed,
      previewMode,
      displayCurrencyCode: cartItemsTotalCurrencyCode,
      allowSendPointsFromStorefront,
      hideBuyPoints: true,
    }),
    [
      storeDetails,
      getStadiumSharedConfig,
      convertedCartItemsTotal,
      onPointsTransferSuccess,
      isGuestOrPreviewMode,
      isStorePointsMigrationAllowed,
      previewMode,
      cartItemsTotalCurrencyCode,
      allowSendPointsFromStorefront,
    ],
  );

  const restricted = RESTRICTED_SOURCES_FOR_BAG_AND_ITEM.includes(source);
  if (restricted) return null;

  return (
    <FundsDetail styles={styles} storeDetails={storeData} authorizedPayments={authorizedPayments} />
  );
};

const UserPointsBalance = () => {
  const showNewHeader = useShowNewHeader();

  return showNewHeader ? <PointsNewBalance /> : <UserOldPointsBalance />;
};

export default UserPointsBalance;
